import { useAccount, useSignTypedData, useWriteContract } from "wagmi";
import { encodeFunctionData } from "viem";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import usePublicClient from "@/hooks/usePublicClient";
import { PERMIT2_ADDRESS } from "@uniswap/permit2-sdk";
import { TypedDataDomain } from "viem";
import ERC20Abi from "@/constants/abis/ERC20.json";
import TransfersAbi from "@/constants/abis/Transfers.json";
import { TransfersAddress } from "@/constants/addresses";
import { DEFAULT_CHAIN_ID } from "@/constants";
import { ethAddress, usdcInfo, wethInfo } from "@/constants/tokens";
import opUrl from "@/constants/opUrl";
import { TokenInfo, useUserInfo } from "@/context/UserInfo";
import { MaxUint256, QUOTER_ADDRESSES } from "@uniswap/sdk-core";
import { accAdd, toFixed } from "@/utils/formatBalance";
import { FeeAmount } from "@uniswap/v3-sdk";
import Quoter from "@/constants/abis/Quoter.json";
import { useNavigate } from "react-router-dom";

export const useTransfer = ({
  orderId,
  token,
  tipAmount,
  feeAmount,
  recipientAmount,
  taxAmount,
  orderAmount,
  recipient,
}: {
  orderId: string | undefined;
  token: TokenInfo | null;
  tipAmount: string;
  feeAmount: string;
  recipientAmount: string;
  taxAmount: string;
  orderAmount: string;
  recipient: string;
}) => {
  const { tokenInfo } = useUserInfo();
  const navigate = useNavigate();
  const { address } = useAccount();
  const [status, setStatus] = useState("");
  const publicClient = usePublicClient();
  const [maxWillingToPay, setMaxWillingToPay] = useState("0");
  const { signTypedDataAsync } = useSignTypedData();
  const { writeContractAsync } = useWriteContract();
  const [swapBeforeError, setSwapBeforeError] = useState("");
  const [swapAfterError, setSwapAfterError] = useState("");
  const [networkFee, setNetworkFee] = useState("0");

  const approveToken = async (args: {
    address: `0x${string}`;
    args: [string, string];
  }) => {
    return writeContractAsync({
      address: args.address,
      abi: ERC20Abi,
      functionName: "approve",
      args: args.args,
    });
  };

  // USDC的Permit签名
  const getUSDCSignature = async ({
    deadline,
    needAmount,
  }: {
    deadline: number;
    needAmount: string;
  }) => {
    if (!token) return;
    const nonce = await publicClient.readContract({
      address: token.address as `0x${string}`,
      abi: ERC20Abi,
      functionName: "nonces",
      args: [address],
    });

    const domain = {
      name: "USDC",
      version: "2",
      chainId: DEFAULT_CHAIN_ID,
      verifyingContract: usdcInfo.address as `0x${string}`,
    };

    const types = {
      Permit: [
        { name: "owner", type: "address" },
        { name: "spender", type: "address" },
        { name: "value", type: "uint256" },
        { name: "nonce", type: "uint256" },
        { name: "deadline", type: "uint256" },
      ],
    };

    const values = {
      owner: address,
      spender: TransfersAddress[DEFAULT_CHAIN_ID],
      value: BigInt(needAmount),
      nonce,
      deadline,
    };

    return await signTypedDataAsync({
      domain,
      types,
      message: values,
      primaryType: "Permit",
    });
  };

  // Permit2的签名
  const getPermit2Signature = async ({
    deadline,
    needAmount,
    tokenAddress,
  }: {
    deadline: number;
    needAmount: string;
    tokenAddress: string;
  }) => {
    const nonce = BigInt(Date.now());

    // 构造符合 permitTransferFrom 方法要求的签名数据
    const domain = {
      name: "Permit2",
      chainId: DEFAULT_CHAIN_ID,
      verifyingContract: PERMIT2_ADDRESS,
    };

    const types = {
      PermitTransferFrom: [
        { name: "permitted", type: "TokenPermissions" },
        { name: "spender", type: "address" },
        { name: "nonce", type: "uint256" },
        { name: "deadline", type: "uint256" },
      ],
      TokenPermissions: [
        { name: "token", type: "address" },
        { name: "amount", type: "uint256" },
      ],
    };

    const values = {
      permitted: {
        token: tokenAddress,
        amount: BigInt(needAmount),
      },
      spender: TransfersAddress[DEFAULT_CHAIN_ID],
      nonce,
      deadline: BigInt(deadline),
    };

    const signature = await signTypedDataAsync({
      domain: domain as TypedDataDomain,
      types,
      message: values,
      primaryType: "PermitTransferFrom",
    });

    return {
      signature,
      nonce,
    };
  };

  const getMaxWillingToPay = async () => {
    if (!token || !orderId) {
      return;
    }

    let maxWillingToPay = "0";
    const parsedUsdcAmount = accAdd(accAdd(orderAmount, tipAmount), taxAmount);

    if (token.address !== usdcInfo.address) {
      // 增加4%的滑点保护,即如果要1usdc,则要求输出为1.04usdc
      const usdcAmountWithSlippage = toFixed(
        ((BigInt(parsedUsdcAmount) * 104n) / 100n).toString(),
        0
      );

      const tokenIn =
        token.address === ethAddress ? wethInfo.address : token.address;

      try {
        const [amountIn] = (await publicClient.readContract({
          address: QUOTER_ADDRESSES[DEFAULT_CHAIN_ID]! as `0x${string}`,
          abi: Quoter,
          functionName: "quoteExactOutputSingle",
          args: [
            {
              tokenIn: tokenIn,
              tokenOut: usdcInfo.address,
              fee: FeeAmount.MEDIUM,
              amount: BigInt(usdcAmountWithSlippage),
              sqrtPriceLimitX96: 0,
            },
          ],
        })) as [BigInt];

        maxWillingToPay = amountIn.toString();
      } catch (e) {
        setSwapBeforeError("Get max willing to pay error");
        setMaxWillingToPay("0");
      }
    } else {
      maxWillingToPay = parsedUsdcAmount;
    }
    setMaxWillingToPay(maxWillingToPay);
  };

  const getNetworkFee = async () => {
    if (!token) {
      setNetworkFee("0");
      return;
    }

    // 如果是 USDC，gas fee 为 0
    if (token.address === usdcInfo.address) {
      setNetworkFee("0");
      return;
    }

    try {
      // 获取当前 gas price
      const gasPrice = await publicClient.getGasPrice();
      // 使用固定的 gas limit 600000
      const estimatedFee = gasPrice * 600000n;

      setNetworkFee(estimatedFee.toString());
    } catch (error) {
      console.error("获取 gas fee 失败:", error);
      setNetworkFee("0");
    }
  };

  const checkEthBalanceForGas = async () => {
    if (maxWillingToPay === "0") {
      return;
    }

    if (!address || !token || Object.keys(tokenInfo).length === 0) {
      return;
    }

    const balance = tokenInfo?.[ethAddress]?.balance ?? "0";
    if (BigInt(balance) < BigInt(networkFee)) {
      setSwapBeforeError(
        `Insufficient ${tokenInfo?.[ethAddress]?.symbol} balance`
      );
      return;
    }

    if (token.address === ethAddress) {
      const totalRequired = BigInt(networkFee) + BigInt(maxWillingToPay);
      if (BigInt(balance) < totalRequired) {
        setSwapBeforeError(
          `Insufficient ${tokenInfo[ethAddress].symbol} balance`
        );
        return;
      }
      setSwapBeforeError("");
    } else {
      const tokenBalance = tokenInfo[token.address]?.balance ?? "0";
      if (BigInt(tokenBalance) < BigInt(maxWillingToPay)) {
        setSwapBeforeError(
          `Insufficient ${tokenInfo[token.address].symbol} balance`
        );
        return;
      }
    }

    setSwapBeforeError("");
  };

  const transfer = async () => {
    if (!token || !orderId || maxWillingToPay === "0") return;
    setStatus("loading");

    try {
      const deadline = Math.floor(Date.now() / 1000) + 3600;

      const transferIntent = {
        recipientAmount: BigInt(recipientAmount),
        deadline: BigInt(deadline),
        recipient: recipient,
        recipientCurrency: usdcInfo.address as `0x${string}`,
        refundDestination: address,
        feeAmount: BigInt(feeAmount),
        taxAmount: BigInt(taxAmount),
        tipAmount: BigInt(tipAmount),
        id: Number(orderId),
      };

      let data;
      let hash;

      if (token.address === ethAddress) {
        console.log("start pay eth");
        hash = await writeContractAsync({
          address: TransfersAddress[DEFAULT_CHAIN_ID]!,
          abi: TransfersAbi,
          functionName: "swapAndTransferUniswapV3Native",
          args: [transferIntent, FeeAmount.MEDIUM],
          value: BigInt(maxWillingToPay), // 发送ETH数量
        });
      } else if (token.address === usdcInfo.address) {
        // USDC转账
        const signature = await getUSDCSignature({
          deadline,
          needAmount: maxWillingToPay,
        });

        data = encodeFunctionData({
          abi: TransfersAbi,
          functionName: "subsidizedTransferToken",
          args: [transferIntent, { owner: address, signature }],
        });

        // 模拟上链估算gas
        await publicClient.estimateGas({
          account: address,
          to: TransfersAddress[DEFAULT_CHAIN_ID],
          data,
        });

        // 使用 axios 发送交易
        const res = await axios.post(opUrl, { data });
        const result = res.data.result;
        if (result.match(/0x[a-fA-F0-9]{64}/)) {
          // 检查字符串中是否包含0x开头的64位十六进制字符
          const cleanHash = result.replace(/['"\\]/g, "");
          hash = cleanHash;
        } else {
          throw new Error(JSON.stringify(res.data));
        }
      } else {
        // 其他ERC20代币转账
        // 1. 检查 Permit2 的授权额度
        const allowance = (await publicClient.readContract({
          address: token.address as `0x${string}`,
          abi: ERC20Abi,
          functionName: "allowance",
          args: [address, PERMIT2_ADDRESS],
        })) as bigint;

        // 如果授权额度小于需要转账的金额，则需要授权最大值
        if (allowance < BigInt(maxWillingToPay)) {
          const hash = await approveToken({
            address: token.address as `0x${string}`,
            args: [PERMIT2_ADDRESS, MaxUint256.toString()],
          });

          await publicClient.waitForTransactionReceipt({
            hash,
          });
        }

        // 2. 获取 Permit2 签名
        const { signature, nonce } = await getPermit2Signature({
          deadline,
          needAmount: maxWillingToPay, // 使用maxWillingToPay作为允许转账的最大数量
          tokenAddress: token.address,
        });

        // 用户自己提交交易
        hash = await writeContractAsync({
          address: TransfersAddress[DEFAULT_CHAIN_ID]!,
          abi: TransfersAbi,
          functionName: "swapAndTransferUniswapV3Token",
          args: [
            transferIntent,
            {
              permit: {
                permitted: {
                  token: token.address,
                  amount: BigInt(maxWillingToPay), // 使用maxWillingToPay
                },
                spender: TransfersAddress[DEFAULT_CHAIN_ID],
                nonce,
                deadline: BigInt(deadline),
              },
              transferDetails: {
                to: TransfersAddress[DEFAULT_CHAIN_ID],
                requestedAmount: BigInt(maxWillingToPay), // 使用maxWillingToPay
              },
              signature,
            },
            FeeAmount.MEDIUM,
          ],
        });
      }

      await publicClient.waitForTransactionReceipt({
        hash: hash as `0x${string}`,
      });
      setStatus("complete");
      navigate(`/receipt/${orderId}`);
      return hash;
    } catch (error: any) {
      console.log(error, "error");
      if (error.message.includes("User rejected the request")) {
        setStatus("");
        return;
      }

      // 如果错误信息包含autotaskRunId，则说明是openzeppelin defender 的错误
      if (error.message.includes("autotaskRunId")) {
        const errorMessage = JSON.parse(error.message);
        setSwapAfterError(errorMessage.message);
        setStatus("error");
        return;
      }

      let errorMessage = "Payment failed. Please try again";
      // Handle contract-specific errors
      if (error.message.includes("InexactTransfer")) {
        errorMessage = "Inexact transfer amount";
      } else if (error.message.includes("insufficient funds")) {
        errorMessage = "Insufficient funds";
      } else if (error.message.includes("gas required exceeds allowance")) {
        errorMessage = "Gas required exceeds allowance";
      }

      setSwapAfterError(errorMessage);
      setStatus("error");
    }
  };

  const resetError = () => {
    setSwapAfterError("");
    setStatus("");
  };

  useEffect(() => {
    getMaxWillingToPay();
  }, [token, orderId, tipAmount, feeAmount]);

  useEffect(() => {
    getNetworkFee();
  }, [token]);

  useEffect(() => {
    checkEthBalanceForGas();
  }, [tokenInfo, maxWillingToPay, networkFee, token]);

  return {
    transfer,
    status,
    maxWillingToPay,
    swapBeforeError,
    swapAfterError,
    networkFee,
    resetError,
  };
};
